'use client';

import dynamic from 'next/dynamic';
import classNames from 'classnames';
import { Segment } from '@fixter/i18n';
import { useAppContext } from '$util/AppContext';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { recordAnalytics } from '$util/analytics';
import { AnalyticsEvents } from '$util/analytics/types';
import type { VrmLookupV2Props } from '$quantum/molecules/VrmLookupV2';
import type { ButtonProps } from '$src-components/atoms/Button';
import { useModalQueue } from '$util/hooks/useModalQueue';
import { getSegmentFromLocale } from '$util/getSegment';
import { Button } from '@/components/atoms/Button';
import { LoadingIndicator } from '@/components/atoms/LoadingIndicator';
import { getCmsData } from './cms';

import styles from './index.module.scss';

/**
 * @note Dynamically import to reduces bundle size because it's needed only after user interaction.
 */
const VrmLookupModalContent = dynamic(
  () => import('./VrmLookupModalContent').then((mod) => mod.VrmLookupModalContent),
  {
    loading: () => (
      <div className={styles.loadingContent}>
        <LoadingIndicator />
      </div>
    ),
  }
);

export interface VrmLookupModalProps {
  readonly id: string;
  readonly toggleButton?: ButtonProps;
  readonly fullWidth?: boolean;
  readonly formProps?: VrmLookupV2Props;
}

/**
 * @deprecated use from `src`
 */
export function VrmLookupModal({
  id,
  toggleButton: {
    className: toggleButtonClassName,
    onClick: onToggleClick,
    children: toggleButtonChildren,
    ...toggleButtonProps
  } = {},
  formProps = {},
}: VrmLookupModalProps & ButtonProps): JSX.Element {
  const {
    cmsContent: { dictionary },
    location: { pathname },
    locale,
  } = useAppContext<AppContextGlobal>();

  const [, queueModal] = useModalQueue();

  const { toggleText, modalTitle, modalCta, ctToggleText } = getCmsData(dictionary);

  const toggleModal: React.MouseEventHandler = () => {
    recordAnalytics(AnalyticsEvents.GET_INSTANT_QUOTE_MODAL_CLICKED);
    queueModal([
      <VrmLookupModalContent
        key="content"
        title={modalTitle}
        formProps={{
          className: classNames(styles.modalVrmLookupForm, formProps.className),
          buttonText: modalCta,
          ...formProps,
        }}
      />,
      'modal',
    ]);
  };

  const isCTPage = getSegmentFromLocale(locale) === Segment.Fixter_FR && pathname === '/controle-technique'; // EEEwww! Yuck! Disgusting! Don't do this! It's bad for your health.

  const toggleButtonLabel = isCTPage ? ctToggleText : toggleText;

  return (
    <Button id={id} className={toggleButtonClassName} onClick={toggleModal} {...toggleButtonProps}>
      {toggleButtonChildren ?? toggleButtonLabel}
    </Button>
  );
}
VrmLookupModal.displayName = 'VrmLookupModal';

export default VrmLookupModal;
